import { resolveTenantSlug } from "@/hooks/useTenantPath";
import { useTenantStore } from "@/stores/tenantStore";

const PRIMARY_TENANT_KEY = "braise_last_tenant";

const storeTenantSlug = (slug: string) => {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(PRIMARY_TENANT_KEY, slug);
  } catch {
    /* noop */
  }
};

const POST_AUTH_STORAGE_KEY = "braise:post-auth-redirect";
const POST_AUTH_TTL_MS = 5 * 60 * 1000;

const normalizeSegment = (segment: string) => {
  if (!segment) {
    return "";
  }
  return segment.startsWith("/") ? segment.slice(1) : segment;
};

const getStoreSlug = () => {
  try {
    const store = useTenantStore.getState();
    return store.slug ?? store.activeSubdomain ?? null;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn("[tenantNavigation] unable to access tenant store", error);
    }
    return null;
  }
};

export const buildTenantPath = (relativePath: string, fallbackPath: string) => {
  const storeSlug = getStoreSlug();
  const fallbackSlug = resolveTenantSlug(null);
  const slug = storeSlug ?? fallbackSlug ?? null;

  if (slug) {
    const normalized = normalizeSegment(relativePath);
    return `/r/${slug}/${normalized}`;
  }

  return fallbackPath;
};

export const getTenantDashboardPath = (fallback = "/dashboard") => {
  return buildTenantPath("dashboard", fallback);
};

const DEFAULT_BASE_URL = "http://localhost";

const toRelativePath = (url: URL) => `${url.pathname}${url.search}${url.hash}`;

export const resolveTenantDashboardPath = (destination = "/dashboard") => {
  if (!destination.startsWith("/dashboard")) {
    return destination;
  }

  const basePath = destination.split(/[?#]/)[0] || "/dashboard";
  const tenantDashboard = getTenantDashboardPath(basePath);

  if (destination === basePath) {
    return tenantDashboard;
  }

  try {
    const targetUrl = new URL(tenantDashboard, DEFAULT_BASE_URL);
    const inputUrl = new URL(destination, DEFAULT_BASE_URL);
    targetUrl.search = inputUrl.search;
    targetUrl.hash = inputUrl.hash;
    return toRelativePath(targetUrl);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn("[tenantNavigation] failed to normalize dashboard path", error);
    }
    const suffix = destination.slice(basePath.length);
    return `${tenantDashboard}${suffix}`;
  }
};

export const ensureTenantResolution = async () => {
  try {
    const store = useTenantStore.getState();
    await store.resolveActiveTenant?.().catch((error) => {
      if (import.meta.env.DEV) {
        console.warn("[tenantNavigation] resolveActiveTenant failed", error);
      }
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn("[tenantNavigation] unable to resolve tenant", error);
    }
  }
};

const sanitizeRelativePath = (value: string | null | undefined) => {
  if (!value) {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed.startsWith("/")) {
    return null;
  }
  if (trimmed.startsWith("//")) {
    return null;
  }

  try {
    const url = new URL(trimmed, DEFAULT_BASE_URL);
    return toRelativePath(url);
  } catch {
    return null;
  }
};

const readStoredRedirect = (storage: Storage | undefined | null) => {
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(POST_AUTH_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const payload = JSON.parse(raw) as { path?: string; ts?: number } | null;
    if (!payload?.path) {
      storage.removeItem(POST_AUTH_STORAGE_KEY);
      return null;
    }
    if (payload.ts && Date.now() - payload.ts > POST_AUTH_TTL_MS) {
      storage.removeItem(POST_AUTH_STORAGE_KEY);
      return null;
    }
    return sanitizeRelativePath(payload.path);
  } catch {
    return null;
  }
};

const writeStoredRedirect = (storage: Storage | undefined | null, path: string | null) => {
  if (!storage) {
    return;
  }
  try {
    if (!path) {
      storage.removeItem(POST_AUTH_STORAGE_KEY);
      return;
    }
    storage.setItem(POST_AUTH_STORAGE_KEY, JSON.stringify({ path, ts: Date.now() }));
  } catch {
    // Ignore storage quota issues
  }
};

const clearStoredRedirect = () => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    writeStoredRedirect(window.sessionStorage, null);
  } catch {
    /* noop */
  }
  try {
    writeStoredRedirect(window.localStorage, null);
  } catch {
    /* noop */
  }
};

export const normalizeRequestedPath = (value: string | null | undefined) => sanitizeRelativePath(value);

export const rememberPostAuthRedirect = (value: string | null | undefined) => {
  const sanitized = sanitizeRelativePath(value);
  if (typeof window !== "undefined") {
    try {
      writeStoredRedirect(window.sessionStorage, sanitized);
    } catch {
      /* noop */
    }
    try {
      writeStoredRedirect(window.localStorage, sanitized);
    } catch {
      /* noop */
    }
  }
  return sanitized;
};

export const consumePostAuthRedirect = (fallback?: string | null) => {
  let stored: string | null = null;
  if (typeof window !== "undefined") {
    try {
      stored = readStoredRedirect(window.sessionStorage);
      if (!stored) {
        stored = readStoredRedirect(window.localStorage);
      }
    } catch {
      stored = null;
    }
  }

  clearStoredRedirect();

  if (stored) {
    return stored;
  }

  return sanitizeRelativePath(fallback);
};

export interface TenantSessionLike {
  id?: string | null;
  slug?: string | null;
  name?: string | null;
}

export const applyTenantFromSession = (tenant: TenantSessionLike | null | undefined): string | null => {
  if (!tenant?.id || !tenant.slug) {
    return null;
  }

  try {
    const store = useTenantStore.getState();
    store.setActiveTenant?.(tenant.id, tenant.slug, tenant.name ?? tenant.slug, false);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn("[tenantNavigation] failed to persist tenant from session", error);
    }
  }

  storeTenantSlug(tenant.slug);

  return tenant.slug;
};

export const determinePostAuthRedirect = (requestedPath?: string | null) => {
  const sanitized = sanitizeRelativePath(requestedPath);

  if (sanitized && !sanitized.startsWith("/dashboard")) {
    return sanitized;
  }

  const fallback = sanitized ?? "/dashboard";
  return resolveTenantDashboardPath(fallback);
};
